<template>
<div id="purchases">
  <h2>Purchase Reports</h2>

  <purchase-nav></purchase-nav>

  <p class="alert alert-warning text-center" role="alert">
    Purchase reports are currently generated from locally stored purchase data &ndash;
    not retrieved directly from merchants.
  </p>

  <div v-if="this.reports.start_at">
    <hr />

    <header id="report-header">
      <h4>
        Purchase Report from {{ this.reports.start_at }} to {{ this.reports.end_at }}
      </h4>
      <button
        class="btn btn-light btn-sm"
        v-if="!this.reports.length"
      >
        🖨️ Invoice
      </button>
    </header>
    <purchase-reports-invoice
      :start-at="this.reports.start_at"
      :end-at="this.reports.end_at"
      :invoice-total="this.invoiceTotal"
    />

    <div
      v-if="this.reports.count > 0"
    >
      <table
        class="table text-center"
      >
        <th>
          Purchase Count
        </th>
        <th>
          Gross
        </th>
        <th>
          Transaction Fee
        </th>
        <th>
          Net
        </th>
        <tr>
          <td>
            {{ reports.count }}
          </td>
          <td>
            ${{ calculatedGross }}
          </td>
          <td>
            ${{ calculatedFee }}
          </td>
          <td>
            ${{ calculatedNet }}
          </td>
        </tr>
      </table>
      <h5>
        Purchase Details
      </h5>
      <div
        id="purchasable-items-report"
      >
        <table
          class="table text-center"
        >
          <th>
            Count
          </th>
          <th>
            Item
          </th>
          <tr
            v-for="item in sorted_purchasable_items"
            :key="item.id"
          >
            <td>
              {{ item.count }}
            </td>
            <td>
              {{ item.purchasable_item.name }}
            </td>
          </tr>
        </table>
      </div>
      <ol>
        <li
          v-for="purchase in reports.purchases"
          :key="purchase.id"
        >
          {{  purchase }}
        </li>
      </ol>
    </div>
    <p v-else>
      No Purchases Found
    </p>

    <hr />
  </div>

  <form
    :action="this.apiUrl('/purchases/reports_by_range', true)"
    method="post"
    v-on:submit.prevent="submit"
  >
    <fieldset>
      <legend>Report Criteria By Date Range</legend>
      <div class="form-group row">
        <div class="col">
          <div class="calendar">
            <label
              for="purchase_start_at"
            >
              Start Date
            </label>

            <input
              type="date"
              class="form-control"
              name="start_at"
              id="purchase_start_at"
              v-model="startAt"
            />
          </div>
        </div>

        <div class="col">
          <div class="calendar">
            <label
              for="purchase_end_at"
            >
              End Date
            </label>

            <input
              type="date"
              class="form-control"
              name="end_at"
              id="purchase_end_at"
              v-model="endAt"
            />
          </div>
        </div>
      </div>
      <button
        ref="submit"
        type="submit"
        class="btn btn-primary btn-block form-control"
      >
        <span>Get Reports</span>
        <vue-simple-spinner size="small" v-if="loading"></vue-simple-spinner>
      </button>
    </fieldset>
    </form>

    <form
      :action="this.apiUrl('/purchases/reports_by_month', true)"
      method="post"
      v-on:submit.prevent="submit"
    >
    <fieldset class="mt-4">
      <legend>Report Criteria By Month/Year</legend>

      <div class="form-group row">
        <div class="col-2">
          <label
            for="purchase_month"
          >
            By Month
          </label>
        </div>
        <div class="col-5">
          <select
            name="month"
            id="purchase_month"
            class="form-control"
          >
            <option
              value=""
            >
              Select Month
            </option>
            <option value="1" selected>January</option>
            <option
              v-for="month in months"
              v-bind:value="month.value"
            >
              {{ month.name }}
            </option>
          </select>
        </div>

        <div class="col-5">
          <select
            name="year"
            id="purchase_year"
            class="form-control"
          >
            <option
              value=""
            >
              Select Year
            </option>
            <option selected>2024</option>
            <option
              v-for="year in years"
              v-bind:value="year"
            >
              {{ year }}
            </option>
          </select>
        </div>
      </div>

    </fieldset>

    <button
      ref="monthYearSubmit"
      type="submit"
      class="btn btn-primary btn-block form-control"
    >
      <span>Get Reports</span>
      <vue-simple-spinner size="small" v-if="loading"></vue-simple-spinner>
    </button>
  </form>
</div>
</template>

<script>
import Pagination from 'sai-vue-kaminari'

import Spinner from 'vue-simple-spinner'

import PurchaseNav from '@/components/PurchaseNav'
import PurchaseReportsInvoice from '@/components/PurchaseReportsInvoice'

import PurchaseStore from '@/stores/PurchaseStore'

export default {
  components: {
    pagination: Pagination,
    'purchase-nav': PurchaseNav,
    'purchase-reports-invoice': PurchaseReportsInvoice,
    'vue-simple-spinner': Spinner
  },

  data: function () {
    let state = PurchaseStore.state
    state.loading = false
    state.reports = {}
    state.month = ''
    state.year = undefined
    state.start_at = undefined
    state.end_at = undefined
    state.actionUrl = undefined

    return state
  },

  computed: {
    defaultMonth () {
      return new Date().getUTCMonth()
    },

    defaultYear () {
      return new Date().getUTCFullYear()
    },

    months () {
      return this.$root.months.map((month, i) => {
        return {
          name: month,
          value: (i + 1)
        }
      })
    },

    invoiceTotal () {
      return Number(this.reports.gross * 0.01).toFixed(2)
    },

    calculatedGross () {
      return Number(this.reports.gross).toFixed(2)
    },

    calculatedFee () {
      return Number(this.reports.fee).toFixed(2)
    },

    calculatedNet () {
      return (Number(this.reports.gross) + Number(this.reports.fee)).toFixed(2)
    },

    years () {
      const thisYear = this.defaultYear
      const startYear = 2012

      let ys = []
      for (let i = startYear; i <= thisYear; i++) {
        ys.push(i)
      }

      return ys.reverse()
    },

    endAt () {
      const d = new Date(this.defaultYear, this.defaultMonth)

      return this.$root.dateFmt(d)
    },

    startAt () {
      const d = new Date(this.defaultYear, (this.defaultMonth - 1))
      return this.$root.dateFmt(d)
    },

    sorted_purchasable_items () {
      return this.reports.purchasable_items.sort((a, b) => {
        if (a.count > b.count) {
          return -1
        } else if (a.count < b.count) {
          return 1
        } else {
          return 0
        }
      })
    }
  },

  methods: {
    submit (ev) {
      this.loading = true

      this.bindSubmit(ev).then((resp) => {
        this.loading = false

        this.reports = this.processSuccess(resp)
      }, (resp) => {
        this.loading = false
        this.processErrors(resp)
      })
    }
  }
}
</script>

<style scoped>
th {
  text-align: center;
}

#report-header {
  display: flex;
  justify-content: space-between;
}

#purchasable-items-report {
  max-height: 350px;
  overflow-y: auto;
}
</style>



// WEBPACK FOOTER //
// src/components/PurchaseReports.vue